import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { IconButton } from '@/app/components/buttons/IconButton';
import { PhoneInput } from '@/app/components/form/PhoneInput';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import { CONTACT_TITLES } from '@/app/(main)/client-detail/utils/contacts';
import type { ClientPhonesFieldPrefix } from './types-util';
import DangerIcon from '/public/icons/delete.svg';
import PlusIcon from '/public/icons/plus.svg';

interface ClientPhonesProps<T extends CreateClientFormData = CreateClientFormData> {
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
  fieldPrefix: ClientPhonesFieldPrefix<T>;
  isSmallDeleteButton?: boolean;
  additionalPhonesLabel?: string;
}

export const ClientPhones = ({
  onUpdateFormData,
  isSmallDeleteButton,
  additionalPhonesLabel = 'Additional number',
}: ClientPhonesProps) => {
  const {
    formState: { errors },
    setError,
    clearErrors,
    setValue,
    watch,
  } = useFormContext<CreateClientFormData>();

  const { fields, append, remove } = useFieldArray({
    name: 'primary_passenger.client.phones',
  });

  const primaryPhone = watch('primary_passenger.client.phone');

  const isPrimaryPhoneValid = useMemo(() => {
    if (!primaryPhone || primaryPhone.trim() === '') return false;
    if (errors?.primary_passenger?.client?.phone) return false;
    if (primaryPhone.length < 3) return false;
    return true;
  }, [primaryPhone, errors?.primary_passenger?.client?.phone]);

  const onDeletePhone = (index: number) => {
    remove(index);
    onUpdateFormData?.(
      'primary_passenger.client.phones',
      fields.filter((_, i) => i !== index)
    );
  };

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);
  };

  return (
    <div className="flex flex-col gap-5 max-md:gap-4">
      <PhoneInput
        name="primary_passenger.client.phone"
        value={primaryPhone || ''}
        disableDialCodePrefill
        onChange={(v) =>
          setValue('primary_passenger.client.phone', v?.phoneWithoutMask ?? '', { shouldValidate: true })
        }
        setError={setError}
        clearErrors={clearErrors}
        label="Primary phone number"
        placeholder="Enter phone number"
        errors={errors}
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      {fields.map((field, index) => (
        <div key={field.id} className={clsx('flex items-start relative', isSmallDeleteButton ? 'relative' : 'gap-2')}>
          <PhoneInput
            name={`primary_passenger.client.phones.${index}.phone`}
            value={watch(`primary_passenger.client.phones.${index}.phone`) || ''}
            label={additionalPhonesLabel}
            placeholder="Enter phone number"
            onChange={(v) => {
              setValue(`primary_passenger.client.phones.${index}.phone`, v?.phoneWithMask ?? '', {
                shouldValidate: true,
              });
            }}
            errors={errors}
            setError={setError}
            className="w-full"
            clearErrors={clearErrors}
            onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
            valueType="phoneWithMask"
          />
          {isSmallDeleteButton ? (
            <button onClick={() => onDeletePhone(index)} className="cursor-pointer">
              <DangerIcon className="absolute top-0 right-0 w-4 h-4 text-red-500" />
            </button>
          ) : (
            <div className="mt-7">
              <IconButton
                color="danger"
                size="xl"
                onClick={() => onDeletePhone(index)}
                icon={<DangerIcon className="w-6 h-6 text-red-500" />}
              />
            </div>
          )}
        </div>
      ))}

      <BaseButton
        intent="inline"
        startIcon={<PlusIcon className="w-4 h-4" />}
        type="button"
        disabled={!isPrimaryPhoneValid}
        onClick={() => append({ phone: '', title: CONTACT_TITLES.secondary })}
        className="text-[16px]"
      >
        Add Another Number
      </BaseButton>
    </div>
  );
};
